import { InputV2 } from '@bridebook/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { IPinFormFields } from '@bridebook/web/lib/wedding-website/types';
import { getPinFormValidationSchema } from '@bridebook/web/lib/wedding-website/utils/validation';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/button';
import { styles } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/password-page/password-form.styles';

type Props = {
  onPasswordEntered: (pin: string) => void;
  isInvalidPinError: boolean;
  isFetching: boolean;
};

export const PasswordForm = ({ onPasswordEntered, isInvalidPinError, isFetching }: Props) => {
  const { t } = useTranslation('weddingWebsite');

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    clearErrors,
  } = useForm<IPinFormFields>({
    resolver: zodResolver(getPinFormValidationSchema(t)),
    defaultValues: {
      pin: '',
    },
  });

  useEffect(() => {
    if (isInvalidPinError && !isFetching) {
      setError('pin', {
        type: 'invalid_pin',
        message: t('validation.pin.invalid'),
      });
    }
  }, [isInvalidPinError, setError, isFetching, t]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (event: string | ChangeEvent<Element>) => void,
    ) => {
      if (isInvalidPinError) {
        clearErrors('pin');
      }
      onChange(e);
    },
    [isInvalidPinError, clearErrors],
  );

  const onSubmit = (data: IPinFormFields) => {
    onPasswordEntered(data.pin);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      name="password-entry-form"
      className="flex flex-col gap-8 @screen-lg:gap-20">
      <Controller
        control={control}
        name="pin"
        render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
          <InputV2
            value={value}
            onChange={(e) => handleChange(e, onChange)}
            onBlur={onBlur}
            name={name}
            label={t('previewMode.password.label')}
            placeholder={t('previewMode.password.placeholder')}
            disabled={isFetching || isSubmitting}
            error={error?.message}
            inputStyle={styles.font}
            labelStyle={styles.font}
            style={styles.wrapperStyle}
          />
        )}
      />
      <Button type="submit" loading={isFetching || isSubmitting}>
        {t('previewMode.password.cta')}
      </Button>
    </form>
  );
};
